import React, { useState } from 'react';
import PremiumLayout from '../components/PremiumLayout';
import MarkdownRenderer from '../components/MarkdownRenderer';
import JsonFormatter from '../components/JsonFormatter';
import apiClient from '../api/client';
import { LightBulbIcon } from '@heroicons/react/24/outline';

const Insights: React.FC = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const [responseData, setResponseData] = useState<any>(null);
  const [isJsonResponse, setIsJsonResponse] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResponse('');
    setResponseData(null);
    setIsJsonResponse(false);

    try {
      const result = await apiClient.post('/insights/query', { query });
      if (result.data.success) {
        const data = result.data.data;
        
        // Check if response contains JSON data
        if (data.data && Array.isArray(data.data)) {
          // It's a structured JSON response with data array
          setResponseData(data);
          setIsJsonResponse(true);
          setResponse(''); // Clear text response
        } else if (data.answer) {
          // It's a text answer
          setResponse(data.answer);
          setIsJsonResponse(false);
        } else if (data.message) {
          // Try to parse message as JSON
          try {
            const parsed = JSON.parse(data.message);
            if (parsed.data && Array.isArray(parsed.data)) {
              setResponseData(parsed);
              setIsJsonResponse(true);
              setResponse('');
            } else {
              setResponse(data.message);
              setIsJsonResponse(false);
            }
          } catch {
            setResponse(data.message);
            setIsJsonResponse(false);
          }
        } else {
          setResponse('No response received');
          setIsJsonResponse(false);
        }
      } else {
        setResponse(`Error: ${result.data.error || 'Unknown error'}`);
        setIsJsonResponse(false);
      }
    } catch (error: any) {
      console.error('Insights API error:', error);
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Error getting insights';
      setResponse(`❌ Error: ${errorMessage}`);
      setIsJsonResponse(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PremiumLayout
      title="AI Insights"
      subtitle="Get AI-powered insights about your CRM data"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="premium-card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <LightBulbIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ask AI Anything</h2>
            <p className="text-gray-500">Get intelligent insights about your CRM data</p>
          </div>
          
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="flex space-x-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., What are my top opportunities this month?"
                className="input-premium flex-1"
              />
              <button
                type="submit"
                disabled={loading || !query}
                className="btn-primary px-8"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </span>
                ) : (
                  'Ask AI'
                )}
              </button>
            </div>
          </form>
          
          {(response || responseData) && (
            <div className="premium-card bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-8 border border-indigo-100 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <LightBulbIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      AI Response
                    </span>
                  </h3>
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    {isJsonResponse && responseData ? (
                      <JsonFormatter data={responseData} />
                    ) : (
                      <MarkdownRenderer content={response} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PremiumLayout>
  );
};

export default Insights;

